<template>
  <div class="store-management">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <div>
            <span>店铺管理</span>
            <p class="header-desc">维护门店信息，供小程序首页与下单页选择门店、计算距离</p>
          </div>
          <el-button class="btn-primary" icon="el-icon-plus" @click="openCreate">新增门店</el-button>
        </div>
      </template>

      <el-table :data="stores" v-loading="loading" style="width: 100%">
        <el-table-column prop="name" label="门店名称" min-width="120" />
        <el-table-column prop="address" label="地址" min-width="180" show-overflow-tooltip />
        <el-table-column prop="phone" label="联系电话" width="120" />
        <el-table-column label="营业时间" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.business_hours_text || '-' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 'open'" type="success" size="small">营业中</el-tag>
            <el-tag v-else-if="scope.row.status === 'closed'" type="info" size="small">休息中</el-tag>
            <el-tag v-else type="danger" size="small">已禁用</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" class="btn-secondary" @click="openEdit(scope.row)">编辑</el-button>
            <el-button
              size="mini"
              class="btn-secondary"
              @click="toggleStatus(scope.row)"
            >{{ scope.row.status === 'disabled' ? '启用' : '禁用' }}</el-button>
            <el-button size="mini" type="danger" plain @click="removeStore(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="dialogMode === 'create' ? '新增门店' : '编辑门店'"
      :visible.sync="dialogVisible"
      width="720px"
      top="5vh"
      destroy-on-close
      append-to-body
      custom-class="app-admin-dialog"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <div class="grid-2">
          <el-form-item label="门店名称" prop="name">
            <el-input v-model="form.name" maxlength="50" show-word-limit placeholder="例如：EATing 温岭店" />
          </el-form-item>
          <el-form-item label="联系电话">
            <el-input v-model="form.phone" maxlength="20" placeholder="门店电话" />
          </el-form-item>
        </div>
        <el-form-item label="门店地址" prop="address">
          <div class="addr-row">
            <el-input v-model="form.address" maxlength="200" placeholder="详细地址，如：浙江省台州市温岭市城西街道横湖中路372-15" />
            <el-button
              type="primary"
              plain
              :loading="geocoding"
              :disabled="!String(form.address || '').trim()"
              @click="geocodeFromAddress"
            >根据地址自动识别</el-button>
          </div>
        </el-form-item>
        <div class="grid-2">
          <el-form-item label="纬度 latitude（南北，约 15–55）">
            <el-input v-model="form.latitude" placeholder="可自动识别，或手填：28.3720000" />
          </el-form-item>
          <el-form-item label="经度 longitude（东西，约 100–130）">
            <el-input v-model="form.longitude" placeholder="可自动识别，或手填：121.3600000" />
          </el-form-item>
        </div>
        <p class="form-tip">
          填写地址后点「根据地址自动识别」即可写入经纬度（高德 GCJ-02）。
          保存时若未填坐标也会自动按地址解析。门店位置固定，一般无需每日改；服务器每天会按地址自动校准一次。
          注意不要填反：纬度一般是 28.x，经度一般是 121.x。
        </p>
        <div class="grid-2">
          <el-form-item label="配送半径（公里）">
            <el-input-number v-model="form.delivery_radius_km" :min="0.5" :max="50" :step="0.5" :precision="1" style="width:100%" />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="form.status" style="width:100%">
              <el-option label="营业中" value="open" />
              <el-option label="休息中" value="closed" />
              <el-option label="禁用" value="disabled" />
            </el-select>
          </el-form-item>
        </div>
        <el-form-item label="门店封面图">
          <el-upload
            class="cover-uploader"
            :show-file-list="false"
            :http-request="handleImageUpload"
            accept="image/jpeg,image/png,image/webp"
          >
            <img v-if="form.cover_image" :src="resolveImg(form.cover_image)" class="cover-preview" />
            <div v-else class="cover-placeholder"><i class="el-icon-plus" /> 上传封面</div>
          </el-upload>
        </el-form-item>
        <el-form-item label="门店简介">
          <el-input type="textarea" :rows="2" v-model="form.description" maxlength="300" show-word-limit />
        </el-form-item>
        <el-form-item label="营业时间（每周）">
          <div class="hours-table">
            <div v-for="day in weekDays" :key="day.key" class="hours-row">
              <el-checkbox v-model="form.business_hours[day.key].enabled">{{ day.label }}</el-checkbox>
              <el-time-select
                v-model="form.business_hours[day.key].start"
                :disabled="!form.business_hours[day.key].enabled"
                placeholder="开始"
                :picker-options="{ start: '06:00', step: '00:30', end: '23:30' }"
                style="width: 110px;"
              />
              <span class="hours-sep">-</span>
              <el-time-select
                v-model="form.business_hours[day.key].end"
                :disabled="!form.business_hours[day.key].enabled"
                placeholder="结束"
                :picker-options="{ start: '06:00', step: '00:30', end: '23:30' }"
                style="width: 110px;"
              />
            </div>
          </div>
          <el-button type="text" @click="applyWeekdayHours">一键：周一至周五 10:00-21:00，周末休息</el-button>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button class="btn-primary" :loading="saving" @click="submit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { apiBase } from '../api/client'
import { formatApiError } from '../utils/apiError'

const WEEK_DAYS = [
  { key: 'mon', label: '周一' },
  { key: 'tue', label: '周二' },
  { key: 'wed', label: '周三' },
  { key: 'thu', label: '周四' },
  { key: 'fri', label: '周五' },
  { key: 'sat', label: '周六' },
  { key: 'sun', label: '周日' }
]

function emptyHours() {
  const h = {}
  WEEK_DAYS.forEach((d) => {
    h[d.key] = { enabled: true, start: '10:00', end: '21:00' }
  })
  return h
}

export default {
  name: 'StoreManagement',
  data() {
    return {
      weekDays: WEEK_DAYS,
      stores: [],
      loading: false,
      saving: false,
      geocoding: false,
      dialogVisible: false,
      dialogMode: 'create',
      editingId: null,
      form: {
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        phone: '',
        cover_image: '',
        description: '',
        delivery_radius_km: 5,
        status: 'open',
        business_hours: emptyHours()
      },
      rules: {
        name: [{ required: true, message: '请输入门店名称', trigger: 'blur' }],
        address: [{ required: true, message: '请输入门店地址', trigger: 'blur' }]
      }
    }
  },
  computed: {
    assetOrigin() {
      return String(apiBase || '').replace(/\/api\/?$/, '') || (typeof window !== 'undefined' ? window.location.origin : '')
    }
  },
  mounted() {
    this.loadStores()
  },
  methods: {
    resolveImg(url) {
      const s = String(url || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s)) return s
      if (s.startsWith('/')) return this.assetOrigin + s
      return s
    },
    async loadStores() {
      this.loading = true
      try {
        const resp = await this.$api.get('/stores/admin/all')
        if (resp && resp.code === 200) {
          this.stores = resp.data || []
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载门店失败'))
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.dialogMode = 'create'
      this.editingId = null
      this.form = {
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        phone: '',
        cover_image: '',
        description: '',
        delivery_radius_km: 5,
        status: 'open',
        business_hours: emptyHours()
      }
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogMode = 'edit'
      this.editingId = row.id
      const hours = emptyHours()
      const src = row.business_hours || {}
      WEEK_DAYS.forEach((d) => {
        if (src[d.key]) {
          hours[d.key] = {
            enabled: src[d.key].enabled !== false,
            start: src[d.key].start || '10:00',
            end: src[d.key].end || '21:00'
          }
        }
      })
      this.form = {
        name: row.name || '',
        address: row.address || '',
        latitude:
          row.latitude != null && row.latitude !== ''
            ? String(row.latitude)
            : '',
        longitude:
          row.longitude != null && row.longitude !== ''
            ? String(row.longitude)
            : '',
        phone: row.phone || '',
        cover_image: row.cover_image || '',
        description: row.description || '',
        delivery_radius_km: Number(row.delivery_radius_km) || 5,
        status: row.status || 'open',
        business_hours: hours
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (!this.form.latitude || !this.form.longitude) {
          this.$message.warning('该门店尚未保存经纬度，请填写后保存，否则小程序距离不准')
        }
      })
    },
    applyWeekdayHours() {
      WEEK_DAYS.forEach((d) => {
        const isWeekend = d.key === 'sat' || d.key === 'sun'
        this.form.business_hours[d.key] = {
          enabled: !isWeekend,
          start: '10:00',
          end: '21:00'
        }
      })
    },
    async handleImageUpload(param) {
      if (!param.file) return
      const formData = new FormData()
      formData.append('file', param.file)
      try {
        const resp = await this.$api.post('/products/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          this.form.cover_image = resp.data.url
          this.$message.success('上传成功')
        } else {
          this.$message.error('上传失败')
        }
      } catch (e) {
        this.$message.error('上传失败')
      } finally {
        param.onSuccess && param.onSuccess()
      }
    },
    async geocodeFromAddress() {
      const address = String(this.form.address || '').trim()
      if (!address) {
        this.$message.warning('请先填写门店地址')
        return
      }
      this.geocoding = true
      try {
        const resp = await this.$api.post('/stores/admin/geocode', { address })
        const data = resp && resp.data
        if (resp && resp.code === 200 && data && data.latitude != null && data.longitude != null) {
          this.form.latitude = String(data.latitude)
          this.form.longitude = String(data.longitude)
          this.$message.success(
            data.formatted_address
              ? `已识别：${data.latitude}, ${data.longitude}`
              : '已自动识别经纬度'
          )
        } else {
          this.$message.error((resp && resp.message) || '识别失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '识别失败，请检查高德 Key 是否开通 Web 服务'))
      } finally {
        this.geocoding = false
      }
    },
    submit() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        let lat = String(this.form.latitude || '').trim()
        let lng = String(this.form.longitude || '').trim()
        // 未填坐标时先自动识别
        if ((!lat || !lng) && String(this.form.address || '').trim()) {
          try {
            this.geocoding = true
            const resp = await this.$api.post('/stores/admin/geocode', {
              address: this.form.address
            })
            const data = resp && resp.data
            if (resp && resp.code === 200 && data) {
              this.form.latitude = String(data.latitude)
              this.form.longitude = String(data.longitude)
              lat = String(data.latitude)
              lng = String(data.longitude)
            }
          } catch (e) {
            this.$message.error(formatApiError(e, '地址识别失败，请手动填写经纬度'))
            this.geocoding = false
            return
          } finally {
            this.geocoding = false
          }
        }
        if (!lat || !lng) {
          this.$message.error('请填写纬度与经度，或先填写地址后自动识别')
          return
        }
        const latNum = Number(lat)
        const lngNum = Number(lng)
        if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
          this.$message.error('经纬度格式不正确，请填写数字，例如 28.372 / 121.360')
          return
        }
        if (latNum < 15 || latNum > 55 || lngNum < 70 || lngNum > 140) {
          this.$message.warning('坐标看起来不在中国常见范围，请确认是否填反（纬度约28，经度约121）')
        }
        this.saving = true
        try {
          const payload = {
            name: this.form.name,
            address: this.form.address,
            latitude: latNum,
            longitude: lngNum,
            phone: this.form.phone,
            cover_image: this.form.cover_image,
            description: this.form.description,
            delivery_radius_km: this.form.delivery_radius_km,
            status: this.form.status,
            business_hours: this.form.business_hours
          }
          let resp
          if (this.dialogMode === 'create') {
            resp = await this.$api.post('/stores', payload)
            this.$message.success('添加成功')
          } else {
            resp = await this.$api.put(`/stores/${this.editingId}`, payload)
            this.$message.success('保存成功')
          }
          const saved = resp && resp.data
          if (saved && (saved.latitude == null || saved.longitude == null)) {
            this.$message.error('保存后经纬度仍为空，请检查服务器数据库是否有 latitude/longitude 字段并重启 API')
          }
          this.dialogVisible = false
          await this.loadStores()
        } catch (e) {
          this.$message.error(formatApiError(e, '保存失败'))
        } finally {
          this.saving = false
        }
      })
    },
    async toggleStatus(row) {
      const next = row.status === 'disabled' ? 'open' : 'disabled'
      try {
        await this.$api.put(`/stores/${row.id}`, { ...row, status: next, business_hours: row.business_hours })
        this.$message.success(next === 'disabled' ? '已禁用' : '已启用')
        await this.loadStores()
      } catch (e) {
        this.$message.error('操作失败')
      }
    },
    async removeStore(row) {
      try {
        await this.$confirm(`确定删除门店「${row.name}」吗？`, '提示', { type: 'warning' })
        await this.$api.delete(`/stores/${row.id}`)
        this.$message.success('已删除')
        await this.loadStores()
      } catch (e) {
        /* cancel */
      }
    }
  }
}
</script>

<style scoped>
.store-management { padding: 20px; }
.addr-row {
  display: flex;
  gap: 10px;
  width: 100%;
  align-items: center;
}
.addr-row .el-input {
  flex: 1;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.header-desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: #909399;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
}
.form-tip {
  margin: -8px 0 12px;
  font-size: 12px;
  color: #909399;
}
.hours-table { width: 100%; }
.hours-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.hours-sep { color: #999; }
.cover-preview {
  width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.cover-placeholder {
  width: 120px;
  height: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed #cfcfcf;
  border-radius: 8px;
  color: #888;
  font-size: 12px;
  background: #fafafa;
}
@media (max-width: 720px) {
  .grid-2 { grid-template-columns: 1fr; }
}
</style>
